import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { submitKyc, uploadFile } from "@/services/supabaseService";
import useStoreSettings from "@/hooks/useStoreSettings";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

export default function KycApply() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { lineUrl } = useStoreSettings();

  const [realName, setRealName] = useState("");
  const [idNumber, setIdNumber] = useState("");
  const [frontPhoto, setFrontPhoto] = useState("");
  const [backPhoto, setBackPhoto] = useState("");
  const [holdingPhoto, setHoldingPhoto] = useState("");
  const [msg, setMsg] = useState("");
  const [msgType, setMsgType] = useState<"" | "success" | "error">("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingType, setUploadingType] = useState<"" | "front" | "back" | "holding">("");

  const frontInputRef = useRef<HTMLInputElement>(null);
  const backInputRef = useRef<HTMLInputElement>(null);
  const holdingInputRef = useRef<HTMLInputElement>(null);

  const kycStatus = user?.kyc_status || "未認證";

  const handleFileSelect = async (type: "front" | "back" | "holding", file: File | null) => {
    if (!file || !user) return;
    setMsg("");
    setMsgType("");
    setUploadingType(type);

    try {
      // Validate file type and size (max 5MB)
      if (!file.type.startsWith("image/")) {
        setMsg("請上傳圖片檔案（JPG、PNG）");
        setMsgType("error");
        setUploadingType("");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setMsg("檔案大小超過 5MB 限制，請壓縮後重新上傳");
        setMsgType("error");
        setUploadingType("");
        return;
      }

      // Upload via Edge Function (bypasses Storage RLS)
      const ext = file.name.split(".").pop() || "jpg";
      const path = `${user.id}/${type}_${Date.now()}.${ext}`;

      const { url: publicUrl } = await uploadFile(file, "kyc", path);
      if (!publicUrl || !publicUrl.startsWith("http")) {
        throw new Error("上傳後未取得有效的圖片網址，請聯繫管理員");
      }

      if (type === "front") setFrontPhoto(publicUrl);
      if (type === "back") setBackPhoto(publicUrl);
      if (type === "holding") setHoldingPhoto(publicUrl);
    } catch (err: unknown) {
      console.error("[KycUpload] Unexpected error:", err);
      setMsg(err instanceof Error ? err.message : "圖片上傳發生意外錯誤");
      setMsgType("error");
    } finally {
      setUploadingType("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg("");
    setMsgType("");

    if (!user) {
      setMsg("請先登入");
      setMsgType("error");
      return;
    }

    if (!realName.trim() || !idNumber.trim()) {
      setMsg("請完整填寫真實姓名與身分證字號");
      setMsgType("error");
      return;
    }

    if (!frontPhoto || !backPhoto) {
      setMsg("請上傳身分證正反面照片");
      setMsgType("error");
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await submitKyc({
        user_id: user.id,
        user_name: user.name,
        user_email: user.email,
        real_name: realName.trim(),
        id_number: idNumber.trim(),
        front_photo: frontPhoto,
        back_photo: backPhoto,
        holding_photo: holdingPhoto || null,
      });
      setMsg(`實名認證申請 ${result.id} 已提交，預計 1 個工作日內完成審核`);
      setMsgType("success");
    } catch (err: unknown) {
      setMsg(err instanceof Error ? err.message : "提交失敗，請稍後重試");
      setMsgType("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const statusConfig: Record<
    string,
    { title: string; desc: string; color: string; icon: string; bg: string }
  > = {
    未認證: {
      title: "尚未完成實名認證",
      desc: "為保障您的資金安全，提現前需完成實名認證。請準備身分證件，填寫以下資料並上傳照片。",
      color: "text-amber-700",
      icon: "ri-shield-check-line",
      bg: "bg-amber-50 border-amber-200",
    },
    審核中: {
      title: "實名認證審核中",
      desc: "您的認證資料已提交，管理員正在審核中。預計 1 個工作日內完成，請耐心等待。",
      color: "text-blue-700",
      icon: "ri-time-line",
      bg: "bg-blue-50 border-blue-200",
    },
    已通過: {
      title: "實名認證已通過",
      desc: "恭喜！您的實名認證已通過審核，現在可以正常使用提現功能。",
      color: "text-emerald-700",
      icon: "ri-check-double-line",
      bg: "bg-emerald-50 border-emerald-200",
    },
    已拒絕: {
      title: "實名認證未通過",
      desc: `您的認證申請已被拒絕。原因：${user?.kyc_admin_note || "資料不符合要求"}。請修正後重新提交。`,
      color: "text-red-700",
      icon: "ri-close-circle-line",
      bg: "bg-red-50 border-red-200",
    },
  };

  const currentStatus = statusConfig[kycStatus] || statusConfig["未認證"];
  const showForm = kycStatus === "未認證" || kycStatus === "已拒絕";

  const renderUploadZone = (
    type: "front" | "back" | "holding",
    label: string,
    required: boolean,
    hint: string,
    photo: string,
    setPhoto: (v: string) => void,
    inputRef: React.RefObject<HTMLInputElement | null>
  ) => {
    const isUploading = uploadingType === type;

    return (
      <div>
        <label className="text-sm text-gray-600 mb-2 block">
          {label} {required && <span className="text-red-400">*</span>}
          {!required && <span className="text-gray-400 font-normal">（可選，增加安全性）</span>}
        </label>
        {photo ? (
          <div className="relative group">
            <img
              src={photo}
              alt={label}
              className="w-full h-48 object-cover rounded-lg border border-gray-200"
              onError={() => {
                setMsg(`${label} 圖片載入失敗，網址無效`);
                setMsgType("error");
                setPhoto("");
                if (inputRef.current) inputRef.current.value = "";
              }}
            />
            <div className="absolute top-2 right-2 flex gap-1.5">
              <button
                type="button"
                onClick={() => {
                  setPhoto("");
                  if (inputRef.current) inputRef.current.value = "";
                  inputRef.current?.click();
                }}
                className="w-7 h-7 bg-black/60 text-white rounded-full flex items-center justify-center hover:bg-black/80 cursor-pointer"
                title="重新上傳"
              >
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-refresh-line text-xs"></i>
                </div>
              </button>
              <button
                type="button"
                onClick={() => {
                  setPhoto("");
                  if (inputRef.current) inputRef.current.value = "";
                }}
                className="w-7 h-7 bg-black/60 text-white rounded-full flex items-center justify-center hover:bg-black/80 cursor-pointer"
                title="移除"
              >
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-close-line text-xs"></i>
                </div>
              </button>
            </div>
            <button
              type="button"
              onClick={() => window.open(photo, "_blank")}
              className="absolute bottom-2 right-2 px-2.5 py-1 bg-black/60 text-white rounded text-xs hover:bg-black/80 cursor-pointer"
            >
              查看大圖
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={isUploading}
            className="w-full h-48 rounded-lg border-2 border-dashed border-gray-300 hover:border-amber-400 flex flex-col items-center justify-center gap-2 transition-colors cursor-pointer group disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isUploading ? (
              <>
                <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center animate-spin">
                  <i className="ri-loader-2-line text-amber-600 text-lg"></i>
                </div>
                <p className="text-xs text-gray-500">上傳中...</p>
              </>
            ) : (
              <>
                <div className="w-10 h-10 rounded-full bg-gray-100 group-hover:bg-amber-50 flex items-center justify-center transition-colors">
                  <i className="ri-upload-cloud-2-line text-gray-400 group-hover:text-amber-600 text-lg"></i>
                </div>
                <p className="text-xs text-gray-500">點擊上傳{label}</p>
                <p className="text-xs text-gray-400">{hint}</p>
              </>
            )}
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            handleFileSelect(type, e.target.files?.[0] || null);
            e.target.value = "";
          }}
        />
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />

      <div className="pt-20 pb-12">
        <div className="px-6 lg:px-12 max-w-3xl mx-auto">
          <div className="flex items-center gap-2 mb-6 text-sm text-gray-500">
            <button onClick={() => navigate("/dashboard")} className="hover:text-amber-600 cursor-pointer">
              個人中心
            </button>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-xs"></i>
            </div>
            <span className="text-gray-700">實名認證</span>
          </div>

          <div className={`rounded-xl border p-5 mb-5 ${currentStatus.bg}`}>
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${currentStatus.bg.split(" ")[0]}`}>
                <i className={`${currentStatus.icon} ${currentStatus.color} text-lg`}></i>
              </div>
              <div>
                <h2 className={`text-base font-semibold ${currentStatus.color}`}>{currentStatus.title}</h2>
                <p className="text-sm text-gray-600 mt-1 leading-relaxed">{currentStatus.desc}</p>
                {kycStatus === "審核中" && (
                  <p className="text-xs text-gray-400 mt-2">提交時間：{user?.kyc_submitted_at ? new Date(user.kyc_submitted_at).toLocaleString("zh-TW") : ""}</p>
                )}
                {kycStatus === "已通過" && (
                  <p className="text-xs text-gray-400 mt-2">通過時間：{user?.kyc_reviewed_at ? new Date(user.kyc_reviewed_at).toLocaleString("zh-TW") : ""} · 實名姓名：{user?.kyc_real_name}</p>
                )}
                {kycStatus === "已拒絕" && user?.kyc_reviewed_at && (
                  <p className="text-xs text-gray-400 mt-2">審核時間：{new Date(user.kyc_reviewed_at).toLocaleString("zh-TW")}</p>
                )}
              </div>
            </div>
          </div>

          {showForm && (
            <div className="bg-white rounded-xl border border-gray-100 p-6 mb-5">
              <h2 className="text-base font-semibold text-gray-900 mb-5 flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-id-card-line text-amber-600 text-sm"></i>
                </div>
                實名認證申請
              </h2>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-600 mb-1.5 block">
                      真實姓名 <span className="text-red-400">*</span>
                      <span className="text-xs text-gray-400 font-normal ml-1">（需與銀行開戶名一致）</span>
                    </label>
                    <input
                      type="text"
                      value={realName}
                      onChange={(e) => { setRealName(e.target.value); setMsg(""); }}
                      placeholder="例如：王小明"
                      className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 mb-1.5 block">
                      身分證字號 / 居留證號 <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={idNumber}
                      onChange={(e) => { setIdNumber(e.target.value.toUpperCase()); setMsg(""); }}
                      placeholder="例如：A123456789"
                      maxLength={10}
                      className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <p className="text-sm text-gray-700 font-medium">證件照片上傳</p>

                  {renderUploadZone(
                    "front",
                    "身分證正面",
                    true,
                    "支援 JPG、PNG 格式，最大 5MB",
                    frontPhoto,
                    setFrontPhoto,
                    frontInputRef
                  )}

                  {renderUploadZone(
                    "back",
                    "身分證反面",
                    true,
                    "支援 JPG、PNG 格式，最大 5MB",
                    backPhoto,
                    setBackPhoto,
                    backInputRef
                  )}

                  {renderUploadZone(
                    "holding",
                    "手持證件照",
                    false,
                    "手持證件靠近臉部，確保文字清晰",
                    holdingPhoto,
                    setHoldingPhoto,
                    holdingInputRef
                  )}
                </div>

                {msg && (
                  <div className={`rounded-lg px-3 py-2.5 text-sm border flex items-start gap-2 ${
                    msgType === "success" ? "bg-emerald-50 border-emerald-200 text-emerald-600" : "bg-red-50 border-red-200 text-red-600"
                  }`}>
                    <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                      <i className={`${msgType === "success" ? "ri-check-line" : "ri-error-warning-line"} text-xs`}></i>
                    </div>
                    {msg}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={!realName.trim() || !idNumber.trim() || !frontPhoto || !backPhoto || isSubmitting || uploadingType !== ""}
                  className="w-full py-3 rounded-xl bg-amber-500 text-white font-medium text-sm hover:bg-amber-600 transition-all active:scale-[0.98] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 flex items-center justify-center animate-spin"><i className="ri-loader-2-line text-sm"></i></div>
                      提交中...
                    </span>
                  ) : (
                    "提交實名認證申請"
                  )}
                </button>
              </form>
            </div>
          )}

          <a
            href={lineUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full py-2.5 rounded-xl text-white font-medium text-sm hover:opacity-90 transition-all active:scale-[0.98] flex items-center justify-center gap-2 cursor-pointer"
            style={{ backgroundColor: "#06C755" }}
          >
            <div className="w-5 h-5 flex items-center justify-center"><i className="ri-chat-1-line"></i></div>
            認證遇到問題？私訊團長 (LINE)
          </a>
        </div>
      </div>

      <Footer />
    </div>
  );
}
